// Lightweight Markdown renderer — handles headers, bold, italic, lists, tables, code, citations.
// Built from scratch to avoid pulling react-markdown + remark + rehype (~50KB) for v1.
// Mermaid code fences are rendered as SVG via a lazy-loaded mermaid import.

import { useEffect, useMemo, useRef } from 'react';
import type { Brain } from '../types';

interface Props {
  text: string;
  className?: string;
  /** Brains used to produce this text. Powers citation hover-cards on [N] tags. */
  brains?: Brain[];
}

export function Markdown({ text, className = '', brains }: Props) {
  const html = useMemo(() => renderMarkdown(text), [text]);
  const ref = useRef<HTMLDivElement>(null);

  // Wire citation hovers — each [N] superscript gets a tooltip showing the
  // brain name + author. Numbering is 1-indexed against the brains array.
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const cites = root.querySelectorAll('sup.md-cite');
    if (cites.length === 0 || !brains || brains.length === 0) return;
    cites.forEach((sup) => {
      const txt = (sup.textContent ?? '').replace(/[\[\]\s]/g, '');
      if (!txt) return;
      const indices = txt.split(',').map((s) => parseInt(s, 10)).filter((n) => Number.isFinite(n));
      const labels: string[] = [];
      indices.forEach((n) => {
        const brain = brains[n - 1];
        if (brain) labels.push(`[${n}] ${brain.name} — ${brain.org} · ${brain.author}`);
      });
      if (labels.length > 0) {
        sup.setAttribute('title', labels.join('\n'));
        (sup as HTMLElement).style.cursor = 'help';
      }
    });
  }, [html, brains]);

  // After the markdown HTML is in the DOM, find any mermaid placeholders and
  // render them as SVG. The mermaid library is lazy-imported so initial bundle
  // stays light for users who never view a flowchart.
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const nodes = root.querySelectorAll('div.mermaid-placeholder[data-mermaid-src]');
    if (nodes.length === 0) return;
    let cancelled = false;
    (async () => {
      const mermaidMod = await import('mermaid');
      if (cancelled) return;
      const mermaid = mermaidMod.default;
      mermaid.initialize({
        startOnLoad: false,
        theme: 'base',
        themeVariables: {
          fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
          primaryColor: '#fafaf9',
          primaryTextColor: '#1d1d1d',
          primaryBorderColor: '#d4d4d4',
          lineColor: '#666666',
          secondaryColor: '#f0efed',
          tertiaryColor: '#fff',
        },
      });
      for (let i = 0; i < nodes.length; i++) {
        const el = nodes[i] as HTMLElement;
        const rawSrc = decodeURIComponent(el.getAttribute('data-mermaid-src') || '');
        const id = `mermaid-svg-${Date.now()}-${i}`;
        // Try to render the original source first. If mermaid throws (commonly
        // from gantt task names that contain ":" or other delimiters mermaid
        // treats as syntax), sanitize and retry.
        let lastErr: any = null;
        let rendered = false;
        for (const src of [rawSrc, sanitizeMermaid(rawSrc)]) {
          try {
            const { svg } = await mermaid.render(id, src);
            el.innerHTML = svg;
            el.classList.add('mermaid-rendered');
            el.removeAttribute('data-mermaid-src');
            rendered = true;
            break;
          } catch (err: any) {
            lastErr = err;
          }
        }
        if (!rendered) {
          el.innerHTML = `<div class="mermaid-error">Couldn't render diagram: ${escape(lastErr?.message ?? String(lastErr))}</div><pre class="md-pre"><code>${escape(rawSrc)}</code></pre>`;
          el.classList.add('mermaid-failed');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [html]);

  return <div ref={ref} className={`md-content ${className}`} dangerouslySetInnerHTML={{ __html: html }} />;
}

function renderMarkdown(src: string): string {
  // Tokenize line-by-line for block-level elements
  const lines = src.split('\n');
  const out: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Horizontal rule — three or more dashes/asterisks/underscores on a line
    if (/^\s*(?:-\s*-\s*-+|\*\s*\*\s*\*+|_\s*_\s*_+)\s*$/.test(line)) {
      out.push('<hr class="md-hr" />');
      i++;
      continue;
    }

    // Blockquote
    if (/^>\s?/.test(line)) {
      const quoted: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        quoted.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      out.push(`<blockquote class="md-bq">${inline(quoted.join(' '))}</blockquote>`);
      continue;
    }

    // Code fence
    if (line.startsWith('```')) {
      const lang = line.slice(3).trim().toLowerCase();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) { codeLines.push(lines[i]); i++; }
      i++;
      const code = codeLines.join('\n');
      if (lang === 'mermaid') {
        // Placeholder rendered to SVG by the Markdown component's useEffect.
        out.push(`<div class="mermaid-placeholder" data-mermaid-src="${encodeURIComponent(code)}"><div class="mermaid-loading">Rendering diagram…</div></div>`);
      } else {
        out.push(`<pre class="md-pre"><code>${escape(code)}</code></pre>`);
      }
      continue;
    }

    // Table — header row + separator row + body
    if (line.includes('|') && i + 1 < lines.length && /^\s*\|?\s*[-:]+/.test(lines[i + 1])) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i].includes('|')) {
        const trimmed = lines[i].trim();
        if (/^\s*\|?\s*[-:]+/.test(trimmed)) { i++; continue; }
        const cells = splitCells(trimmed);
        rows.push(cells);
        i++;
      }
      if (rows.length >= 1) {
        const [header, ...body] = rows;
        const thead = `<thead><tr>${header.map((c) => `<th>${inline(c)}</th>`).join('')}</tr></thead>`;
        const tbody = `<tbody>${body.map((r) => `<tr>${r.map((c) => `<td>${inline(c)}</td>`).join('')}</tr>`).join('')}</tbody>`;
        out.push(`<table class="md-table">${thead}${tbody}</table>`);
      }
      continue;
    }

    // Headers
    const h = line.match(/^(#{1,4})\s+(.+)/);
    if (h) {
      const level = h[1].length;
      out.push(`<h${level} class="md-h${level}">${inline(h[2])}</h${level}>`);
      i++;
      continue;
    }

    // Unordered list
    if (/^[-*]\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s/, ''));
        i++;
      }
      out.push(`<ul class="md-ul">${items.map((it) => `<li>${inline(it)}</li>`).join('')}</ul>`);
      continue;
    }

    // Ordered list
    if (/^\d+\.\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s/, ''));
        i++;
      }
      out.push(`<ol class="md-ol">${items.map((it) => `<li>${inline(it)}</li>`).join('')}</ol>`);
      continue;
    }

    // Blank line
    if (line.trim() === '') { i++; continue; }

    // Paragraph (collect consecutive non-blank, non-special lines)
    const para: string[] = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].startsWith('```') &&
      !lines[i].match(/^#{1,4}\s/) &&
      !/^[-*]\s/.test(lines[i]) &&
      !/^\d+\.\s/.test(lines[i]) &&
      !(lines[i].includes('|') && i + 1 < lines.length && /^\s*\|?\s*[-:]+/.test(lines[i + 1]))
    ) {
      para.push(lines[i]);
      i++;
    }
    out.push(`<p class="md-p">${inline(para.join(' '))}</p>`);
  }

  return out.join('\n');
}

function inline(s: string): string {
  let out = escape(s);
  // Citations [1] [1,2] → red superscript
  out = out.replace(/\[([\d,\s]+)\]/g, '<sup class="md-cite">[$1]</sup>');
  // Bold
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  // Italic
  out = out.replace(/(?<![*\w])\*([^*]+)\*(?!\w)/g, '<em>$1</em>');
  // Inline code
  out = out.replace(/`([^`]+)`/g, '<code class="md-code">$1</code>');
  return out;
}

function splitCells(row: string): string[] {
  // Strip leading/trailing pipes, then split on pipe with trimming
  const trimmed = row.replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map((c) => c.trim());
}

function escape(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Sanitize mermaid source — removes characters that crash the parser. The big
// failures are gantt task names containing ":" (mermaid's task-id delimiter),
// arrow chars, and Unicode subscripts. Apply only when first render fails.
function sanitizeMermaid(src: string): string {
  return src
    // Replace fancy arrows with ASCII
    .replace(/→/g, '->')
    .replace(/⇒/g, '=>')
    // Unicode subscripts/superscripts → ASCII
    .replace(/[₀₁₂₃₄₅₆₇₈₉]/g, (c) => String('₀₁₂₃₄₅₆₇₈₉'.indexOf(c)))
    .replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹]/g, (c) => String('⁰¹²³⁴⁵⁶⁷⁸⁹'.indexOf(c)))
    // Strip colons inside parens within gantt task names — mermaid uses ":"
    // as the task-id delimiter, and a colon hidden inside "(CO:H₂, T)" makes
    // it parse the wrong segment as the task ID.
    .split('\n')
    .map((line) => {
      // Only process lines that look like gantt tasks (have ":id," shape)
      if (!/:\s*\w+,/.test(line)) return line;
      return line.replace(/\(([^)]*)\)/g, (_m, inner) =>
        '(' + inner.replace(/:/g, ' -') + ')'
      );
    })
    .join('\n');
}
